import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';

// Import components
import Header from './components/Header';
import Banner from './components/Banner';
import {
  ServicesSection,
  FeaturedCoursesSection,
  NewsSection,
} from './components/HomeSections';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Preloader from './components/Preloader';
import Seo from './components/Seo';

// Dedicated pages (routes mirror the original qasc.it URL structure)
import ChiSiamoPage from './pages/ChiSiamoPage';
import FormazionePage from './pages/FormazionePage';
import FormazioneSicurezzaPage from './pages/FormazioneSicurezzaPage';
import AmbienteQualitaPage from './pages/AmbienteQualitaPage';
import ELearningPage from './pages/ELearningPage';
import FormazioneFinanziataPage from './pages/FormazioneFinanziataPage';
import ConsulenzaPage from './pages/ConsulenzaPage';
import CorsiEvidenzaPage from './pages/CorsiEvidenzaPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import ContattiPage from './pages/ContattiPage';

// QAS Consulting brand theme, straight from the tricolor logo:
// petrol #00425c (leaf/Q), green #86a315 (A), crimson #c10a27 (S), with the
// leaf's steel blue #80acc0 as a soft accent. Sora for headings, Manrope for
// body text.
const theme = createTheme({
  palette: {
    primary: {
      main: '#00425c', // Petrol — CTAs and key actions (crimson stays as accent only)
      dark: '#023144',
      light: '#598ba1',
    },
    secondary: {
      main: '#00425c', // Petrol — headings, dark bands
      dark: '#00425c',
      light: '#598ba1',
    },
    success: {
      main: '#86a315', // Olive green — supporting accent
      dark: '#6d8511',
      light: '#9cbb2a',
    },
    background: {
      default: '#ffffff',
      paper: '#e9f1f6',
    },
    text: {
      primary: '#1d2f38',
      secondary: '#48606d',
    },
  },
  typography: {
    fontFamily: '"Manrope", sans-serif',
    h1: {
      fontFamily: '"Sora", sans-serif',
      fontSize: 'clamp(30px, 5vw, 54px)',
      lineHeight: 1.15,
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Sora", sans-serif',
      fontSize: 'clamp(26px, 3.6vw, 38px)',
      lineHeight: 1.25,
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Sora", sans-serif',
      fontSize: 'clamp(21px, 2.6vw, 27px)',
      lineHeight: 1.4,
      fontWeight: 700,
    },
    h4: {
      fontFamily: '"Sora", sans-serif',
      fontSize: 'clamp(19px, 2.2vw, 24px)',
      lineHeight: 1.4,
      fontWeight: 700,
    },
    h5: {
      fontFamily: '"Sora", sans-serif',
      fontSize: '20px',
      lineHeight: 1.45,
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Sora", sans-serif',
      fontSize: '17px',
      lineHeight: 1.5,
      fontWeight: 600,
    },
    body1: {
      fontSize: '15px',
      lineHeight: 1.8,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          textTransform: 'none',
          fontFamily: '"Sora", sans-serif',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
        },
      },
    },
  },
});

const MainContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
}));

// Scroll to top on route (pathname) change, but leave in-page #anchor jumps
// (e.g. /consulenza#modello) alone so the dropdown menu links still work.
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    // Hash present: the target may not be in the DOM yet on a fresh load, so
    // retry until it is.
    const id = decodeURIComponent(hash.replace('#', ''));
    let tries = 0;
    let timer;
    const scrollToHash = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (tries++ < 20) {
        timer = setTimeout(scrollToHash, 50);
      }
    };
    scrollToHash();
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
};

// HomePage component to contain all the sections
const HomePage = () => (
  <>
    <Seo
      title="QAS Consulting — Formazione e Consulenza Aziendale | Qualità, Ambiente, Sicurezza"
      description="QAS Consulting, con sedi a Spinea (VE) e Celano (AQ), offre formazione e consulenza aziendale continua: corsi sulla sicurezza sul lavoro (D.Lgs. 81/08), e-learning, sistemi di gestione, modello organizzativo 231, sicurezza alimentare e formazione finanziata."
      canonical="https://qasc.it/"
    />
    <Header />
    <Banner />
    <ServicesSection />
    <FeaturedCoursesSection />
    <NewsSection />
    <Contact />
    <Footer />
    <ScrollToTop />
  </>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Preloader />
      <Router>
        <ScrollManager />
        <MainContent>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/chi-siamo" element={<ChiSiamoPage />} />
            <Route path="/formazione" element={<FormazionePage />} />
            <Route path="/formazione/formazione-sicurezza" element={<FormazioneSicurezzaPage />} />
            <Route path="/formazione/ambiente-e-qualita" element={<AmbienteQualitaPage />} />
            <Route path="/e-learning" element={<ELearningPage />} />
            <Route path="/formazione-finanziata" element={<FormazioneFinanziataPage />} />
            <Route path="/consulenza" element={<ConsulenzaPage />} />
            <Route path="/corsi-in-evidenza" element={<CorsiEvidenzaPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
            <Route path="/contatti" element={<ContattiPage />} />
            {/* Unknown paths just go home — no 404 page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MainContent>
      </Router>
    </ThemeProvider>
  );
}

export default App;
